"use client";

import { EyeIcon, EyeSlashIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import { useTransition } from "react";
import { toast } from "react-toastify";
import { dismissActivation, restoreActivation } from "../services";

type VisibilityToggleProps = {
  /** Whether the checklist is currently hidden from the dashboard. */
  dismissed: boolean;
  /** Short label for compact placements (dashboard card). */
  compact?: boolean;
  className?: string;
};

export default function VisibilityToggle({
  dismissed,
  compact = false,
  className,
}: VisibilityToggleProps) {
  const [isPending, startTransition] = useTransition();

  const toggle = () => {
    startTransition(async () => {
      const response = dismissed
        ? await restoreActivation()
        : await dismissActivation();
      if (!response.ok) {
        toast.error("Something went wrong. Please try again.");
        return;
      }
      toast.success(
        dismissed
          ? "Checklist is back on your dashboard."
          : "Checklist hidden. You can always find it under Get Started.",
      );
    });
  };

  const Icon = dismissed ? EyeIcon : EyeSlashIcon;
  const label = compact
    ? dismissed
      ? "Show"
      : "Hide"
    : dismissed
      ? "Show checklist on dashboard"
      : "Hide checklist from dashboard";

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={isPending}
      className={clsx(
        "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-white",
        className,
      )}>
      <Icon className="size-4" aria-hidden="true" />
      {isPending ? "Saving…" : label}
    </button>
  );
}
